import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/originalnyi-podarok")!;

const faqItems = [
  { q: "Чем этот подарок отличается от обычных?", a: "Персональная песня существует в единственном экземпляре. Она написана по вашей истории, с именами и деталями." },
  { q: "На какой праздник подойдёт персональная песня?", a: "На любой: день рождения, свадьба, юбилей, годовщина, выпускной — или просто так." },
  { q: "Нужно ли уметь писать стихи?", a: "Нет. Вы просто рассказываете историю своими словами." },
  { q: "Сколько времени нужно на создание?", a: "Около трёх минут." },
  { q: "Сколько стоит оригинальный подарок-песня?", a: "Демоверсия бесплатна. Полная версия — от 249 ₽." },
];

const OriginalnyiPodarok = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">Оригинальный подарок — персональная песня</h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Два часа ночи. Праздник через день. Кружки с надписями? Носки? Сертификат? Всё это уже дарили. Вы хотите что-то, от чего у человека <strong className="text-foreground">перехватит дыхание</strong>.</p>
            <p><strong className="text-foreground">Персональная песня</strong> — трек, написанный с нуля по вашей истории — с музыкой, вокалом и текстом, который попадает точно в сердце.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему персональная песня — самый необычный подарок</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Она единственная. Больше ни у кого такой песни нет. Её невозможно найти в магазине, скопировать, передарить.</p>
              <p>Это подарок, который показывает внимание. Не «я зашёл в магазин», а «я вспомнил нашу историю и превратил её в музыку».</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎁 Создать уникальный подарок</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Кому подарить персональную песню</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground"><Link to="/pesnya-devushke-v-podarok" className="text-primary hover:underline">Девушке или жене</Link>.</strong> Романтическая баллада с вашей историей любви.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-mame-v-podarok" className="text-primary hover:underline">Маме или папе</Link>.</strong> Песня от детей — с воспоминаниями и благодарностью.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-pro-druga" className="text-primary hover:underline">Другу</Link>.</strong> Смешная песня с общими историями и прозвищами.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-kollege-v-podarok" className="text-primary hover:underline">Коллеге</Link>.</strong> Песня с рабочими историями и шутками.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-rebenku-v-podarok" className="text-primary hover:underline">Ребёнку</Link>.</strong> Волшебная песня с его именем и мечтами.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«За двадцать лет мне впервые подарили что-то, что я не могу купить сама»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Жена — женщина, у которой всё есть. Он написал их историю, получил мягкий джаз. Она дослушала, поставила чашку и сказала: «Впервые подарили что-то, что я не могу купить сама». И обняла его.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Подарить то, чего нет ни в одном магазине</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Частые вопросы об оригинальных подарках</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Создайте подарок, который запомнится навсегда</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Персональная песня — подарок, которого достоин каждый.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default OriginalnyiPodarok;
